import {ActionsRow, ff, FixedDropDownButton, i18n} from 'mc/lib';
import {Form, FormActions, FormBody} from 'mc/lib/form';
import {FeatureEditor, FeatureEditorActions} from 'mc/lib/mapio';
import {ModalActions} from 'mc/lib/modal';
import {DescriptionLoader, GeometryLoader} from 'mc/lib/objekty/utils';
import {getMapFromList, requireFields} from 'mc/lib/utils';
import PropTypes from 'prop-types';
import React, {PureComponent} from 'react';
import {connect} from 'react-redux';
import {Alert, Card, CardBlock, CardHeader, Col, DropdownItem, FormGroup, Label, Row} from 'reactstrap';
import {formValueSelector, propTypes, reduxForm} from 'redux-form';
import {Loading} from '../common';
import {DokumentListField} from '../dokument';
import {DruhUcelField} from '../druhucel';
import {ObdobiListField} from '../obdobi';
import {validateObdobiListAsSingleValue} from '../obdobi/utils';
import IdentifikaceForm from './IdentifikaceForm';
import {createFeatureFilterFromObdobiList as createFeatureFilter} from './utils';

const {Tool, EventType} = FeatureEditor;

const featureEditorArea = 'lokalitaFeatureEditor';
const featureEditorColor = [233, 47, 102];

const featureEditorTools = [
    Tool.POINT,
    Tool.LINE,
    Tool.POLYGON,
    Tool.RECTANGLE,
    Tool.SEPARATOR,
    Tool.EDIT_MOVE,
    Tool.ROTATE_MOVE,
    Tool.DELETE,
];

const validateRequiredFields = requireFields([
    'identifikace',
    'druhUcel',
    'delka',
    'sirka',
    'plocha',
], {trimStrings: true});

const validateProvozovnaRequiredFields = requireFields([
    'provozovna',
], {trimStrings: true});

class LokalitaForm extends PureComponent {

    static formName = 'lokalita';

    static propTypes = {
        identifikace: PropTypes.string,
        druhUcel: PropTypes.object,
        druhUcelList: PropTypes.array.isRequired,
        obdobiList: PropTypes.array.isRequired,
        ...propTypes,
    };

    static validate(values) {
        // !!! Při modifikaci, zajistěte konzistenci server-side validací LokalitaVOValidator !!!

        const errors = validateRequiredFields(values);

        const {druhUcel} = values;
        if (druhUcel && druhUcel.provozovna) {
            validateProvozovnaRequiredFields(values, errors);
        }

        errors.obdobiList = validateObdobiListAsSingleValue(values.obdobiList);

        const {geodata} = values;
        if (geodata.length === 0) {
            errors._error = i18n('lokalita.geodata.hint');
        }

        for (const name of ['sirka', 'delka', 'plocha']) {
            const value = values[name];
            if (value != null && value <= 0) {
                errors[name] = i18n('global.error.notPositiveNumber');
            }
        }

        return errors;
    }

    constructor(props) {
        super(props);

        // U některých těchto props si budeme držet jejich stav zároveň na this,
        // abychom měli okamžitý přístup k jejich hodnotě

        const {identifikaceUpravenaRucne, zakresZTextu, geodata} = props.initialValues;

        this.identifikaceUpravenaRucne = identifikaceUpravenaRucne;
        this.zakresZTextu = zakresZTextu;
        this.povolenoGenerovaniZakresuPodleTextu = false;

        this.descriptionLoader = new DescriptionLoader({
            onLoadStateChange: this.updateStateFromDescriptions,
        });

        this.geometryLoader = new GeometryLoader({
            onLoadStateChange: this.updateStateFromGeometries,
            loadDelay: 500,
        });

        this.state = {
            descriptionsLoading: false,
            geometriesLoading: false,
        };

        this.initialFeatures = geodata;
        this.setFeatures(geodata);
    }

    handleFeatureEditorEvent = event => {
        switch (event.type) {
            case EventType.CREATE:
                this.updateFeature(event.feature);
                this.processFeatureChanges();
                break;

            case EventType.UPDATE:
                event.features.forEach(this.updateFeature, this);
                this.processFeatureChanges();
                break;

            case EventType.DELETE:
                event.featureIds.forEach(this.deleteFeature, this);
                this.processFeatureChanges();
                break;
        }
    };

    handleIdentifikaceEdit = () => {
        const {identifikace, dispatch, change} = this.props;
        const formProps = {
            initialValues: {identifikace},
            onSubmit: (data) => {
                this.identifikaceUpravenaRucne = true;
                change('identifikaceUpravenaRucne', true);
                change('identifikace', data.identifikace);
                dispatch(ModalActions.hide());
            },
        };
        dispatch(ModalActions.show(IdentifikaceForm, formProps, i18n('lokalita.identifikace')));
    };

    handleIdentifikaceLoad = () => {
        this.identifikaceUpravenaRucne = false;
        this.props.change('identifikaceUpravenaRucne', false);

        if (this.getFeatures().length > 0) {
            this.loadDescriptionsIfNeeded(true);
        } else {
            this.props.change('identifikace', '');
        }
    };

    handleDefiniceFocus = () => {
        if (this.getFeatures().length === 0) {
            this.povolenoGenerovaniZakresuPodleTextu = true; // Jen pokud uživatel ještě nic nezakreslil
        }
    };

    handleDefiniceBlur = () => {
        this.povolenoGenerovaniZakresuPodleTextu = false;
    };

    handleDefiniceInput = event => {
        if (this.povolenoGenerovaniZakresuPodleTextu) {
            this.geometryLoader.loadGeometries(event.target.value);
        }
    };

    setFeatures(features) {
        this.featureMap = getMapFromList(features);
    }

    getFeatures() {
        return Object.values(this.featureMap);
    }

    updateFeature(feature) {
        this.featureMap[feature.id] = feature;
    }

    deleteFeature(featureId) {
        delete this.featureMap[featureId];
    }

    processFeatureChanges() {
        const features = this.getFeatures();
        if (features.length === 0) {
            this.props.change('zakresZTextu', false); // Byl smazán celý zákres, povolíme jeho zadání textovým vstupem
            this.zakresZTextu = false;
        }
        this.props.change('geodata', features);
        this.loadDescriptionsIfNeeded();
    }

    loadDescriptionsIfNeeded(reloadErrors) {
        // Popisy budeme načítat jen v případě, pokud je alespoň na jednom
        // z polí "idedentifikace", "definice" povoleno jeho zobrazení.
        if (!this.identifikaceUpravenaRucne || !this.zakresZTextu) {
            this.descriptionLoader.loadDescriptions(this.getFeatures(), {reloadErrors});
        }
    }

    updateStateFromDescriptions = () => {
        if (!this.identifikaceUpravenaRucne) {
            this.props.change('identifikace', this.descriptionLoader.getLoadedShortDescriptionsAsString());
        }

        if (!this.zakresZTextu && !this.povolenoGenerovaniZakresuPodleTextu) {
            // Nechceme uživateli přepsat text z něhož nechal vygeneroval zákres.
            // Zároveň mu nesmíme přepisovat hodnotu pokud zrovna edituje pole "definice".
            this.props.change('definice', this.descriptionLoader.getLoadedLongDescriptionsAsString());
        }

        const descriptionsLoading = this.descriptionLoader.isLoading();
        if (this.state.descriptionsLoading !== descriptionsLoading) {
            this.setState({descriptionsLoading});
        }
    };

    updateStateFromGeometries = () => {
        const features = this.geometryLoader.getLoadedGeometriesAsFeatures();

        this.zakresZTextu = features.length > 0;
        this.props.change('zakresZTextu', this.zakresZTextu);
        this.props.dispatch(FeatureEditorActions.loadFeatures(featureEditorArea, features));

        this.setFeatures(features);
        this.processFeatureChanges(); // Až po nastavení příznaku zakresZTextu!

        const geometriesLoading = this.geometryLoader.isLoading();
        if (this.state.geometriesLoading !== geometriesLoading) {
            this.setState({geometriesLoading});
        }
    };

    renderIdentifikace() {
        const {identifikaceUpravenaRucne} = this;
        const {descriptionsLoading, geometriesLoading} = this.state;

        if (geometriesLoading) {
            return <Loading>{i18n('lokalita.definice.loading')}</Loading>;
        }

        if (descriptionsLoading && !identifikaceUpravenaRucne) {
            return <Loading>{i18n('lokalita.identifikace.loading')}</Loading>;
        }

        const {identifikace} = this.props;
        if (identifikace && identifikace.trim()) {
            return identifikace;
        }

        if (identifikaceUpravenaRucne) {
            return <span className="text-danger">{i18n('lokalita.identifikace.upravaHint')}</span>
        }

        // Nechceme text-danger, na chybejici zakres se jiz upozornuje v jinne hlasce
        return <span>{i18n('lokalita.identifikace.zakresHint')}</span>;
    }

    renderIdentifikaceMenu() {
        const {identifikaceUpravenaRucne} = this;
        const {descriptionsLoading} = this.state;

        return (
            <FixedDropDownButton noCaret
                                 title={i18n('lokalita.identifikace.edit')}
                                 disabled={descriptionsLoading && !identifikaceUpravenaRucne}>
                <DropdownItem onClick={this.handleIdentifikaceEdit}>{i18n('lokalita.identifikace.edit.manual')}</DropdownItem>
                <DropdownItem onClick={this.handleIdentifikaceLoad}>{i18n('lokalita.identifikace.edit.load')}</DropdownItem>
            </FixedDropDownButton>
        );
    }

    renderUdajeCardBlock() {
        const {zakresZTextu, povolenoGenerovaniZakresuPodleTextu, featureMap} = this;
        const {descriptionsLoading} = this.state;
        const {druhUcel, druhUcelList} = this.props;

        const noFeatures = Object.keys(featureMap).length === 0;
        const provozovna = druhUcel && druhUcel.provozovna || false;

        return (
            <CardBlock>
                <FormGroup>
                    <Label>{i18n('lokalita.identifikace')}</Label>
                    <ActionsRow actions={this.renderIdentifikaceMenu()}>
                        {this.renderIdentifikace()}
                    </ActionsRow>
                </FormGroup>
                <ff.String name="definice"
                           label={false}
                           placeholder={!zakresZTextu && noFeatures
                               ? i18n('lokalita.definice.zakresHint')
                               : i18n('lokalita.definice')}
                           onFocus={this.handleDefiniceFocus}
                           onBlur={this.handleDefiniceBlur}
                           onInput={this.handleDefiniceInput}
                           disabled={descriptionsLoading && !zakresZTextu && !povolenoGenerovaniZakresuPodleTextu}/>
                <ff.Base name="obdobiList"
                         label={i18n('lokalita.obdobiList')}
                         componentClass={ObdobiListField}/>
                <ff.Textarea rows="4" name="popisUcel" label={i18n('lokalita.popisUcel')}/>
                <Row>
                    <Col xs={12} md={4}>
                        <ff.Number name="sirka" label={i18n('lokalita.sirka') + ' (m)'} double/>
                    </Col>
                    <Col xs={12} md={4}>
                        <ff.Number name="delka" label={i18n('lokalita.delka') + ' (m)'} double/>
                    </Col>
                    <Col xs={12} md={4}>
                        <ff.Number name="plocha" label={i18n('lokalita.plocha') + ' (m²)'} double/>
                    </Col>
                </Row>
                <ff.Base name="druhUcel"
                         label={i18n('lokalita.druhUcel')}
                         componentClass={DruhUcelField}
                         druhUcelList={druhUcelList}/>
                {provozovna && <ff.String name="provozovna" label={i18n('lokalita.provozovna')}/>}
            </CardBlock>
        );
    }

    render() {
        const {handleSubmit, onCancel, submitting, error, obdobiList} = this.props;
        return (
            <Form onSubmit={handleSubmit}>
                <FormBody cards>
                    <Row>
                        <Col xs={12} lg={6} xl={5}>
                            <Card className="mb-3">
                                <CardHeader>
                                    <h5>{i18n('lokalita.title')}</h5>
                                </CardHeader>
                                {this.renderUdajeCardBlock()}
                            </Card>
                            <ff.Base name="dokumentList"
                                     label={null}
                                     componentClass={DokumentListField}/>
                        </Col>
                        <Col xs={12} lg={6} xl={7} className="d-flex flex-column">
                            {error && <Alert color="danger">{error}</Alert>}
                            <FeatureEditor area={featureEditorArea}
                                           filter={createFeatureFilter(obdobiList)}
                                           tools={featureEditorTools}
                                           color={featureEditorColor}
                                           initialFeatures={this.initialFeatures}
                                           onEvent={this.handleFeatureEditorEvent}/>
                        </Col>
                    </Row>
                </FormBody>
                <FormActions storeButton
                             cancelButton
                             onClose={onCancel}
                             disabled={submitting}/>
            </Form>
        );
    }

}

const formConfig = {
    form: LokalitaForm.formName,
    validate: LokalitaForm.validate,
};

const selectFormValue = formValueSelector(LokalitaForm.formName);

const mapStateToProps = state => ({
    identifikace: selectFormValue(state, 'identifikace'),
    druhUcel: selectFormValue(state, 'druhUcel'),
    obdobiList: selectFormValue(state, 'obdobiList'),
});

const connectForm = reduxForm(formConfig);
const connectState = connect(mapStateToProps);

export default connectForm(connectState(LokalitaForm));
